/**
 * PLACEHOLDER component kit — the `ui` layer.
 *
 * Every component here is built strictly from the `tokens` layer: no literal
 * colors, radii, or pixel densities. Superseded by the org design-system kit
 * once its git URL is available (see DESIGN.md / todo.md).
 */
import React from 'react';

const cx = (...parts) => parts.filter(Boolean).join(' ');

/* ---------- Button ---------- */

const BUTTON_VARIANTS = {
  primary: 'bg-primary text-primary-contrast hover:bg-primary-hover border-transparent',
  secondary: 'bg-surface text-text hover:bg-surface-hover border-border',
  ghost: 'bg-transparent text-text-muted hover:bg-surface-hover border-transparent',
  danger: 'bg-danger text-text-inverse border-transparent',
};

export function Button({ variant = 'secondary', className, type = 'button', ...props }) {
  return (
    <button
      type={type}
      className={cx(
        'inline-flex h-control items-center justify-center gap-2 rounded border px-4',
        'text-base font-medium transition-colors disabled:opacity-50',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1',
        'focus-visible:outline-primary',
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...props}
    />
  );
}

/* ---------- Badges & chips ---------- */

export function Badge({ className, style, ...props }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium',
        className,
      )}
      style={style}
      {...props}
    />
  );
}

const STATUS_CLASS = {
  UNKNOWN: 'text-status-unknown',
  INTERESTED: 'text-status-interested',
  CONSULTING: 'text-status-consulting',
  SUBSCRIBER: 'text-status-subscriber',
};

/**
 * Collaboration status = sales pipeline stage. NOT the subscription type
 * (commercial arrangement) — they are distinct concepts (CLAUDE.md §5).
 */
export function StatusBadge({ status, label, className }) {
  return (
    <Badge className={cx('bg-surface-hover', STATUS_CLASS[status], className)}>
      <span aria-hidden="true" className="text-xs leading-none">
        ●
      </span>
      {label ?? status}
    </Badge>
  );
}

/** Tag colors are per-tag data (tag.color_hex), so this is the one component
 *  that legitimately takes a color value — from the API, not from app code. */
export function TagChip({ name, colorHex, className }) {
  return (
    <Badge
      className={cx('border', className)}
      style={{ color: colorHex, borderColor: colorHex, backgroundColor: `${colorHex}14` }}
    >
      {name}
    </Badge>
  );
}

/* ---------- Table (dense: tables over cards) ---------- */

export function Table({ className, ...props }) {
  return (
    <div className="overflow-x-auto rounded border border-border bg-surface">
      <table className={cx('w-full border-collapse text-base', className)} {...props} />
    </div>
  );
}

export function THead(props) {
  return <thead className="bg-surface-hover text-text-muted" {...props} />;
}

export function TBody(props) {
  return <tbody {...props} />;
}

export function TR({ className, interactive, ...props }) {
  return (
    <tr
      className={cx(
        'h-row border-b border-border last:border-b-0',
        interactive && 'cursor-pointer hover:bg-surface-hover',
        className,
      )}
      {...props}
    />
  );
}

export function TH({ className, ...props }) {
  return (
    <th
      scope="col"
      className={cx(
        'h-tablehead px-4 text-start text-sm font-medium whitespace-nowrap',
        className,
      )}
      {...props}
    />
  );
}

export function TD({ className, ...props }) {
  return <td className={cx('px-4 py-2 align-middle', className)} {...props} />;
}

/* ---------- Layout & feedback ---------- */

export function Card({ className, ...props }) {
  return (
    <section
      className={cx('rounded border border-border bg-surface p-5 shadow-sm', className)}
      {...props}
    />
  );
}

export function Field({ label, children, className }) {
  return (
    <div className={cx('flex flex-col gap-1', className)}>
      <span className="text-sm text-text-muted">{label}</span>
      <div className="text-base text-text">{children}</div>
    </div>
  );
}

export function Spinner({ className }) {
  return (
    <span
      role="status"
      aria-label="در حال بارگذاری"
      className={cx(
        'inline-block h-5 w-5 animate-spin rounded-full border-2 border-border',
        'border-t-primary',
        className,
      )}
    />
  );
}

export function EmptyState({ title, hint, className }) {
  return (
    <div className={cx('flex flex-col items-center gap-2 p-7 text-center', className)}>
      <p className="text-md text-text">{title}</p>
      {hint && <p className="text-sm text-text-muted">{hint}</p>}
    </div>
  );
}

/* ---------- Charts (Step 5) ---------- */

/**
 * A BAR CHART, drawn as SVG from the tokens, and NOT a charting library.
 *
 * The brief asked for "a chart lib consistent with the design package". What
 * that requirement actually is — charts must use tokens, not their own palette
 * — is better served by not adding one:
 *
 *  - Every charting library ships its own colour scale, its own font handling
 *    and its own layout units. Making one obey the tokens means overriding all
 *    three, which is more code than drawing rectangles.
 *  - MeshHub is RTL. Chart libraries reason in left-to-right axes; the two
 *    serious ones need per-chart configuration to flip, and the ones that
 *    "support RTL" flip the labels and not the bars.
 *  - This is a bar chart and a table. Adding a dependency for `<rect>` would
 *    also be a dependency the brief did not list, needing a decision the brief
 *    said to ask about.
 *
 * If a deployment ever wants stacked areas or interactive brushing, THAT is
 * when the trade changes; recorded in todo.md.
 *
 * Colours come from `--mh-color-activity-N`, the same slot list the calendar
 * uses (docs/decisions.md #81): a slot list rather than a map, so no code
 * anywhere names a category and a deployment with a ninth wraps around.
 */
const CHART_SLOTS = 8;

export function BarChart({
  data,
  valueLabel,
  //: The ROW is passed too, so a caller can render a secondary figure beside
  //: the primary one — «۱۵ / ۴» for a distribution and its subscriber subset.
  //: Two bars per row would double the height of every card and halve how much
  //: of the page fits on a screen.
  formatValue = (v) => String(v),
  formatLabel = (l) => l,
  emptyLabel = 'داده‌ای نیست',
  //: Optional. When given, each row becomes a button — every number on the
  //: dashboard is meant to be clickable through to the contacts behind it.
  //: Absent by default, because a bar that looks clickable and is not is worse
  //: than one that plainly is not.
  onSelect,
  className,
}) {
  const rows = (data ?? []).filter((d) => d && Number.isFinite(Number(d.value)));
  if (rows.length === 0) {
    return <EmptyState className={className} title={emptyLabel} />;
  }
  const max = Math.max(...rows.map((r) => Number(r.value)), 1);

  // A LIST OF BARS, not an <svg> with computed x/y. RTL then lays every bar out
  // from the right with no `flex-row-reverse` and no transform — the same
  // reasoning that keeps the calendar's Persian week honest (CLAUDE.md §15).
  return (
    <div
      className={cx('flex flex-col gap-2', className)}
      role="img"
      aria-label={valueLabel}
    >
      {rows.map((row, index) => {
        const value = Number(row.value);
        const share = Math.max((value / max) * 100, value > 0 ? 2 : 0);
        const Row = onSelect ? 'button' : 'div';
        return (
          <Row
            key={row.key ?? row.label ?? index}
            type={onSelect ? 'button' : undefined}
            onClick={onSelect ? () => onSelect(row) : undefined}
            className={cx(
              'flex w-full items-center gap-3 text-start',
              onSelect && 'rounded hover:bg-surface-hover',
            )}
          >
            <span
              className="w-chartlabel shrink-0 truncate text-sm text-text-muted"
              title={formatLabel(row.label)}
            >
              {formatLabel(row.label)}
            </span>
            <span className="h-chartbar flex-1 rounded bg-surface-hover">
              <span
                className="block h-full rounded"
                style={{
                  width: `${share}%`,
                  backgroundColor: `var(--mh-color-activity-${(index % CHART_SLOTS) + 1})`,
                }}
              />
            </span>
            <span className="w-chartvalue shrink-0 text-start text-sm tabular-nums">
              {formatValue(value, row)}
            </span>
          </Row>
        );
      })}
    </div>
  );
}
